import { getSlopeBounds } from './SlopeConfig';

type Point = [number, number];

interface SlabPiece {
  points: Point[];
  driftX: number;
  rotation: number;
  delay: number;
}

/** A section of the existing piste that fractures into irregular snow plates. */
export class SnowSlabEffect {
  private width: number;
  private height: number;
  private elapsedMs = 0;
  private active = false;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
  }

  begin() { this.elapsedMs = 0; this.active = true; }
  reset() { this.elapsedMs = 0; this.active = false; }
  update(dtMs: number) {
    if (this.active) this.elapsedMs += Math.max(0, Math.min(dtMs, 100));
  }

  getProgress() { return Math.min(1, this.elapsedMs / 1850); }

  private breakProgress() {
    return Math.max(0, Math.min(1, (this.getProgress() - 0.18) / 0.82));
  }

  private pieces(): SlabPiece[] {
    const slope = getSlopeBounds(this.width);
    const left = Math.max(18, slope.left - this.width * 0.06);
    const right = Math.min(this.width - 18, slope.right + this.width * 0.06);
    const w = right - left;
    return [
      {
        // The upper middle gives way first.
        points: [[left+w*.30,48],[left+w*.70,52],[left+w*.66,132],[left+w*.52,150],[left+w*.34,132]],
        driftX: 0,
        rotation: 0.014,
        delay: 0,
      },
      {
        points: [[left,62],[left+w*.30,48],[left+w*.34,132],[left+w*.18,145],[left,132]],
        driftX: -13,
        rotation: -0.026,
        delay: 0.07,
      },
      {
        points: [[left+w*.70,52],[right,64],[right,135],[left+w*.82,146],[left+w*.66,132]],
        driftX: 13,
        rotation: 0.028,
        delay: 0.10,
      },
      {
        // The lower field follows only after the upper shelf has moved.
        points: [[left+w*.34,132],[left+w*.52,150],[left+w*.66,132],[left+w*.72,225],[left+w*.50,248],[left+w*.28,224]],
        driftX: 1,
        rotation: -0.018,
        delay: 0.24,
      },
      {
        points: [[left,132],[left+w*.18,145],[left+w*.34,132],[left+w*.28,224],[left,242]],
        driftX: -19,
        rotation: -0.042,
        delay: 0.31,
      },
      {
        points: [[left+w*.66,132],[left+w*.82,146],[right,135],[right,240],[left+w*.72,225]],
        driftX: 19,
        rotation: 0.044,
        delay: 0.36,
      },
    ];
  }

  private tracePolygon(ctx: CanvasRenderingContext2D, points: Point[]) {
    ctx.beginPath();
    points.forEach(([x,y], i) => i === 0 ? ctx.moveTo(x,y) : ctx.lineTo(x,y));
    ctx.closePath();
  }

  private pieceProgress(slide: number, delay: number) {
    return Math.max(0, Math.min(1, (slide - delay) / (1 - delay)));
  }

  drawUnder(ctx: CanvasRenderingContext2D) {
    if (!this.active) return;
    const slide = this.breakProgress();
    if (slide <= 0) return;
    const pieces = this.pieces();

    ctx.save();

    // Only the fractured piste footprint opens; the surrounding mountain and
    // rails stay put, making the collapse read as terrain rather than a layer.
    for (const piece of pieces) {
      const local = this.pieceProgress(slide, piece.delay);
      if (local <= 0) continue;
      this.tracePolygon(ctx, piece.points);
      const voidGradient = ctx.createLinearGradient(0, 54, 0, 250);
      voidGradient.addColorStop(0, 'rgba(124,164,188,0.96)');
      voidGradient.addColorStop(0.18, 'rgba(43,78,101,0.98)');
      voidGradient.addColorStop(1, 'rgba(5,18,29,1)');
      ctx.fillStyle = voidGradient;
      ctx.fill();
    }

    // Upper sections separate center-out, then the lower field follows.
    for (let i = 0; i < pieces.length; i++) {
      const piece = pieces[i];
      const local = this.pieceProgress(slide, piece.delay);
      if (local <= 0) continue;
      const eased = local * local;
      const dy = eased * (this.height + 120);
      const cx = piece.points.reduce((sum,p)=>sum+p[0],0)/piece.points.length;
      const cy = piece.points.reduce((sum,p)=>sum+p[1],0)/piece.points.length;
      ctx.save();
      ctx.translate(cx + piece.driftX * eased, cy + dy);
      ctx.rotate(piece.rotation * slide);
      ctx.translate(-cx, -cy);
      this.tracePolygon(ctx, piece.points);
      const snow = ctx.createLinearGradient(0, 55, 0, 245);
      snow.addColorStop(0, 'rgba(255,255,255,0.96)');
      snow.addColorStop(0.72, 'rgba(239,247,251,0.96)');
      snow.addColorStop(1, 'rgba(190,216,230,0.98)');
      ctx.fillStyle = snow;
      ctx.shadowColor = 'rgba(20,48,66,0.48)';
      ctx.shadowBlur = 13;
      ctx.shadowOffsetY = -7;
      ctx.fill();

      // Faint texture lines keep the pieces connected to the piste surface.
      ctx.clip();
      ctx.globalAlpha = 0.20;
      ctx.strokeStyle = '#9fc0d2';
      ctx.lineWidth = 1;
      for (let y = 80; y < 235; y += 31) {
        ctx.beginPath();
        ctx.moveTo(piece.points[0][0]-10, y);
        ctx.quadraticCurveTo(cx, y+8, piece.points[1][0]+60, y-2);
        ctx.stroke();
      }
      ctx.restore();

      // Smaller underside fragments let each section crumble after it breaks,
      // instead of leaving a single pristine plate falling through the void.
      for (let fragment = 0; fragment < 2; fragment++) {
        const fragmentDelay = 0.15 + fragment * 0.13;
        const fp = Math.max(0, Math.min(1, (local - fragmentDelay) / (1 - fragmentDelay)));
        if (fp <= 0) continue;
        const fx = cx + (fragment === 0 ? -1 : 1) * (18 + i * 5);
        const fy = 205 + dy + fp * fp * (70 + fragment * 35);
        ctx.save();
        ctx.translate(fx + piece.driftX * eased, fy);
        ctx.rotate(piece.rotation * 3 * fp + (fragment ? 0.08 : -0.07));
        ctx.fillStyle = fragment ? '#d3e6ef' : '#edf7fb';
        ctx.strokeStyle = 'rgba(75,116,140,0.55)';
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.moveTo(-22,-9);ctx.lineTo(19,-12);ctx.lineTo(25,7);ctx.lineTo(-12,15);ctx.closePath();
        ctx.fill();ctx.stroke();
        ctx.restore();
      }
    }
    ctx.restore();
  }

  drawOver(ctx: CanvasRenderingContext2D) {
    if (!this.active) return;
    const progress = this.getProgress();
    const slide = this.breakProgress();
    const slope = getSlopeBounds(this.width);
    const left = Math.max(18, slope.left - this.width * 0.06);
    const right = Math.min(this.width - 18, slope.right + this.width * 0.06);
    const w = right - left;
    const crackAlpha = Math.min(1, progress * 7);

    ctx.save();
    ctx.strokeStyle = `rgba(67,108,132,${0.25 + crackAlpha * 0.70})`;
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';

    // The first fracture radiates from the upper middle rather than appearing
    // as a rigid full-width cut all at once.
    const primaryCracks: Point[][] = [
      [[left+w*.50,78],[left+w*.39,56],[left+w*.30,48]],
      [[left+w*.50,78],[left+w*.60,61],[left+w*.70,52]],
      [[left+w*.50,78],[left+w*.52,116],[left+w*.52,150]],
    ];
    ctx.globalAlpha = Math.max(0.12, 1 - slide * 1.35);
    primaryCracks.forEach(points => {
      ctx.beginPath();
      points.forEach(([x,y],i)=>i===0?ctx.moveTo(x,y):ctx.lineTo(x,y));
      ctx.stroke();
    });

    // Secondary cracks reach the sides and downhill only as the chain reaction
    // develops, softened by a thinner line and the powder pass below.
    const secondaryAlpha = Math.max(0, Math.min(0.72, (slide - 0.05) * 3));
    if (secondaryAlpha > 0) {
      ctx.globalAlpha = secondaryAlpha;
      ctx.lineWidth = 1.5;
      const secondaryCracks: Point[][] = [
        [[left+w*.30,48],[left+w*.16,58],[left,62]],
        [[left+w*.70,52],[left+w*.84,59],[right,64]],
        [[left+w*.34,132],[left+w*.28,180],[left+w*.28,224]],
        [[left+w*.66,132],[left+w*.72,178],[left+w*.72,225]],
        [[left+w*.52,150],[left+w*.49,205],[left+w*.50,248]],
      ];
      secondaryCracks.forEach(points => {
        ctx.beginPath();
        points.forEach(([x,y],i)=>i===0?ctx.moveTo(x,y):ctx.lineTo(x,y));
        ctx.stroke();
      });
    }

    if (slide > 0) {
      const pieces = this.pieces();
      for (let pieceIndex = 0; pieceIndex < pieces.length; pieceIndex++) {
        const piece = pieces[pieceIndex];
        const local = this.pieceProgress(slide, piece.delay);
        if (local <= 0) continue;
        const dy = local * local * (this.height + 120);
        ctx.globalAlpha = Math.min(0.82, local * 3);
        for (let i = 0; i < 14; i++) {
          const x = piece.points[0][0] + ((i * 0.61803398875) % 1) * (piece.points[1][0] - piece.points[0][0] + w*.12);
          const y = 60 + dy + Math.sin(i * 2.1 + pieceIndex + this.elapsedMs * 0.011) * 13;
          ctx.fillStyle = i % 3 ? '#fbfeff' : '#c5deeb';
          ctx.beginPath();
          ctx.arc(x, y, 2.5 + (i % 6) * 1.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    ctx.restore();
  }
}
